/**
 * Public Templates Service
 *
 * API service for the public landing page — no authentication required.
 * Uses axios directly (not apiClient) to avoid attaching auth headers.
 *
 * Endpoints served by PublicController:
 * - GET  /api/public/templates       — system card templates for the gallery
 * - GET  /api/public/templates/{slug} — one template for the public open-animation page
 * - GET  /api/public/carousel        — admin-curated landing carousel (ordered)
 * - GET  /api/public/event-types     — event types for category tabs
 * - GET  /api/public/event-types/{slug}/tags — catalog tags with counts (chip row)
 * - GET  /api/public/flowers         — flower product previews for the gallery
 * - GET  /api/public/hero-template   — featured template for the hero animation
 * - GET  /api/public/landing-lcp     — first carousel tile metadata + optional seasonal banner
 * - GET  /api/public/seasonal-banner — active seasonal marketing banner (or 204)
 * - POST /api/public/interest        — "Notify Me" email capture
 */

import axios from 'axios';
import { CATALOG_PAGE_SIZE } from '../config/catalogPaging';
import type { CardTemplate, EventType } from '../types';
import { DateSourceType, RecurrenceType } from '../types';
import type { CatalogTagAssignment } from '../utils/catalogTagFilters';
import type { SeasonalBannerSource } from '../constants/seasonalBanner';

/** Public template data returned by GET /public/templates and GET /public/hero-template (PublicCardTemplateDto). */
export interface PublicCardTemplate {
  id: number;
  /** Stable catalog slug (e.g. TPL-001). */
  slug: string;
  name: string;
  description: string;
  /** Parallel arrays — same cardinality as {@link EventTypeIds}. */
  eventTypeIds: number[];
  /** Catalog slugs (EVT-NNN) aligned with {@link EventTypeIds}. */
  eventTypeSlugs: string[];
  /** Display labels aligned with {@link EventTypeIds}. */
  eventTypeNames: string[];
  /** Mantine colour tokens aligned with {@link EventTypeIds}. */
  eventTypeColors: string[];
  mediaToken?: string | null;
  mediaType?: string | null;
  /** Non-destructive 16:9 crop for the card-open preview. */
  mediaFrame?: { x: number; y: number; width: number; height: number } | null;
  /** Video poster still — public carousel lazy-load uses this before mounting &lt;video&gt;. */
  posterMediaToken?: string | null;
  /** Pre-generated WebP carousel variants; JSON uses camelCase `w240` / `w480` / `w720`. */
  thumbnailTokens?: { w240?: string | null; w480?: string | null; w720?: string | null } | null;
  defaultGreetingText: string;
  /** Default email subject line — used by the guest scheduling flow for card customization */
  defaultSubjectLine?: string | null;
  /** Whether the card template uses animated (video) content */
  isAnimated?: boolean;
  /** Catalog tag assignments for chip filtering on public galleries. */
  tagAssignments?: CatalogTagAssignment[];
}

/** Shared metadata returned by public catalog page envelopes (PBI #1118). */
export interface PublicCatalogPageMeta {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasMore: boolean;
}

/** Paged public e-card templates. */
export interface PublicTemplatesPage extends PublicCatalogPageMeta {
  templates: PublicCardTemplate[];
}

/** Paged public flower products. */
export interface PublicFlowersPage extends PublicCatalogPageMeta {
  products: PublicFlowerProduct[];
}

/** Options shared by paged public catalog fetches. */
export interface PublicCatalogPageOptions {
  eventTypeId?: number;
  tagSlugs?: string[];
  page?: number;
  pageSize?: number;
  signal?: AbortSignal;
}

/** One tag row from GET /public/event-types/{slug}/tags (non-archived tags with ≥1 card). */
export interface PublicEventTypeTag {
  slug: string;
  name: string;
  cardCount: number;
}

/**
 * Collapses duplicate template rows returned for multi-event templates on the combined "All" view.
 *
 * @param templates Public gallery rows (possibly duplicated ids).
 */
export function dedupePublicTemplatesById(templates: PublicCardTemplate[]): PublicCardTemplate[] {
  const byId = new Map<number, PublicCardTemplate>();
  for (const t of templates) {
    if (!byId.has(t.id)) {
      byId.set(t.id, t);
    }
  }
  return [...byId.values()];
}

/**
 * Resolves the parallel-array index for the template under the selected tab.
 * On the "All" tab (or unknown ids), prefers the first occasion.
 *
 * @param pub Public template payload.
 * @param allSentinel Tab value representing "every occasion".
 * @param activeTab Current tab value (`allSentinel` or numeric id string).
 */
function getPublicTemplateActiveEventIndex(
  pub: PublicCardTemplate,
  allSentinel: string,
  activeTab: string,
): number {
  if (activeTab === allSentinel) {
    return 0;
  }

  const eventTypeId = parseInt(activeTab, 10);
  if (Number.isNaN(eventTypeId)) {
    return 0;
  }

  const idx = pub.eventTypeIds.indexOf(eventTypeId);
  return idx >= 0 ? idx : 0;
}

/**
 * Resolves Mantine badge/tile accent for the template under the selected tab.
 *
 * @param pub Public template payload.
 * @param allSentinel Tab value representing "every occasion".
 * @param activeTab Current tab value (`allSentinel` or numeric id string).
 */
export function getPublicTemplateTileColor(
  pub: PublicCardTemplate,
  allSentinel: string,
  activeTab: string
): string {
  const idx = getPublicTemplateActiveEventIndex(pub, allSentinel, activeTab);
  return pub.eventTypeColors[idx] ?? pub.eventTypeColors[0] ?? 'violet';
}

/**
 * Resolves the occasion display name for a public e-card tile under the selected tab.
 * Landing Explore uses this above the artwork; shop grids omit the label.
 *
 * @param pub Public template payload.
 * @param allSentinel Tab value representing "every occasion".
 * @param activeTab Current tab value (`allSentinel` or numeric id string).
 */
export function getPublicTemplateTileEventName(
  pub: PublicCardTemplate,
  allSentinel: string,
  activeTab: string,
): string {
  const idx = getPublicTemplateActiveEventIndex(pub, allSentinel, activeTab);
  const tabName = pub.eventTypeNames[idx]?.trim();
  if (tabName) {
    return tabName;
  }

  return getPrimaryPublicCardEventLabel(pub);
}

/**
 * Client-side filtering for Explore/Cards grids when not using server-side filtering.
 *
 * @param templates Raw template list from API.
 * @param allSentinel "All" tab value.
 * @param activeTab Current tab.
 */
export function filterPublicTemplatesByTab(
  templates: PublicCardTemplate[],
  allSentinel: string,
  activeTab: string
): PublicCardTemplate[] {
  const deduped = dedupePublicTemplatesById(templates);

  if (activeTab === allSentinel) {
    return deduped;
  }

  const eventTypeId = parseInt(activeTab, 10);
  if (Number.isNaN(eventTypeId)) {
    return deduped;
  }

  return deduped.filter((t) => t.eventTypeIds.includes(eventTypeId));
}

/**
 * Maps a landing-page {@link PublicCardTemplate} into the richer {@link CardTemplate} shape
 * reused by authenticated scheduling UX (guest mode).
 *
 * @param pub DTO segment from `/public/templates` or `/public/hero-template`.
 */
export function publicCardTemplateToCardTemplate(pub: PublicCardTemplate): CardTemplate {
  const eventTypes = pub.eventTypeIds.map((id, i) => ({
    id,
    slug: pub.eventTypeSlugs[i] ?? '',
    name: pub.eventTypeNames[i] ?? '',
  }));

  return {
    id: pub.id,
    slug: pub.slug,
    name: pub.name,
    description: pub.description,
    eventTypes,
    defaultGreetingText: pub.defaultGreetingText,
    defaultSubjectLine: pub.defaultSubjectLine ?? undefined,
    isAnimated: pub.isAnimated ?? false,
    isSystemTemplate: true,
    mediaToken: pub.mediaToken ?? undefined,
    mediaType: pub.mediaType ?? undefined,
    posterMediaToken: pub.posterMediaToken ?? undefined,
    thumbnailTokens: (() => {
      const tt = pub.thumbnailTokens;
      if (!tt) {
        return undefined;
      }
      const w240 = tt.w240?.trim() || undefined;
      const w480 = tt.w480?.trim() || undefined;
      const w720 = tt.w720?.trim() || undefined;
      if (!w240 && !w480 && !w720) {
        return undefined;
      }
      return { w240, w480, w720 };
    })(),
    tagAssignments: pub.tagAssignments,
  };
}

/** Hero / tile label derived from mapped types (comma-separated names). */
export function getPrimaryPublicCardEventLabel(pub: PublicCardTemplate): string {
  const names = pub.eventTypeNames?.filter(Boolean) ?? [];
  if (names.length === 0) return '';
  if (names.length <= 2) {
    return names.join(' · ');
  }
  return `${names[0]} +${names.length - 1}`;
}

/** Public event type for category tabs and guest scheduling */
export interface PublicEventType {
  id: number;
  /** Stable catalog slug (e.g. EVT-001). */
  slug: string;
  name: string;
  color: string;
  /** Month (1-12) for fixed-date holidays. Null for contact-date events. */
  fixedMonth?: number | null;
  /** Day (1-31) for fixed-date holidays. Null for contact-date events. */
  fixedDay?: number | null;
  /** Name of the contact date field used for scheduling (e.g., "Birthday") */
  dateFieldName: string;
}

/**
 * Maps public event types into the {@link EventType} shape guest modals expect.
 */
export function publicEventTypesToGuestEventTypes(eventTypes: PublicEventType[]): EventType[] {
  return eventTypes.map((pet) => ({
    id: pet.id,
    slug: pet.slug,
    name: pet.name,
    color: pet.color,
    fixedMonth: pet.fixedMonth ?? undefined,
    fixedDay: pet.fixedDay ?? undefined,
    dateFieldName: pet.dateFieldName,
    dateSourceType: pet.fixedMonth != null ? DateSourceType.FixedDate : DateSourceType.ContactDate,
    recurrenceType: RecurrenceType.Yearly,
    recurrenceInterval: 1,
    isSystemDefined: true,
  }));
}

/**
 * Live Florist One admin promotion overlay returned on public/auth flower DTOs (PBI #1234).
 */
export interface PublicFlowerPromotionOverlay {
  id: number;
  publicHeadline: string;
  publicReason: string;
  discountPercent: number;
  salePriceUsd: number;
  listPriceUsd: number;
  /** UTC start of the sale window (JSON-LD Offer validFrom). */
  startsAtUtc: string;
  endsAtUtc: string;
}

/** Public flower product preview for the landing page gallery */
export interface PublicFlowerProduct {
  /** Opaque client-facing product id for deep links and React keys. */
  publicId: string;
  /** Vendor SKU (not a multi-vendor identity key). */
  sku: string;
  name: string;
  description?: string;
  /** Locked Auto Send sentence (public PDP GET-by-publicId only). */
  autopilotCopy?: string | null;
  /** Composed GMC / JSON-LD description (public PDP GET-by-publicId only). */
  merchantDescription?: string | null;
  price: number;
  /** Sale price if currently discounted (vendor or live FO admin promo); null otherwise */
  salePrice?: number | null;
  /**
   * Live Florist One admin promotion overlay (PBI #1234). Present when a sitewide
   * percent-off sale applies to this product.
   */
  activePromotion?: PublicFlowerPromotionOverlay | null;
  /** Advertised florist delivery high-water mark (PDP only). */
  deliveryFromUsd?: number | null;
  /** URL to the product image hosted by the supplier */
  imageUrl?: string | null;
  /**
   * Mantine color name for the best matching flower-eligible event type.
   * Used on public "All" surfaces where there is no active event tab, so
   * flower tiles can still show the same event-colour cue as e-card tiles.
   */
  eventTypeColor?: string | null;
  /**
   * Event type ids mapped from product categories.
   * Used by FlowerOrderModal to filter/preselect occasion on the "All" tab.
   */
  eventTypeIds?: number[];
  supportsDeliveryTracking?: boolean;
  supportsMorningDelivery?: boolean;
  supportsAddOns?: boolean;
  supportsDeliveryLocationType?: boolean;
  supportsSubstitutions?: boolean;
  avoidSundayDelivery?: boolean;
  /** Google Merchant Center offer id when the product is feed-published. */
  merchantItemId?: string | null;
  /** Return policy and delivery windows for Product JSON-LD (public PDP only). */
  listingPolicy?: import('../types/merchantListingPolicy').PublicMerchantListingPolicy | null;
  /**
   * UTC instant when the current list price last became authoritative.
   * Maps to schema.org Offer validFrom when no live admin promotion applies.
   */
  offerValidFromUtc?: string | null;
}

/**
 * Active seasonal marketing banner from GET /public/seasonal-banner
 * (and nested on GET /public/landing-lcp).
 */
export interface PublicSeasonalBanner {
  /** Banner origin: holiday event type or evergreen AppSettings fallback. */
  source: SeasonalBannerSource;
  /** Event type slug when source is holiday (for explore-tab selection). */
  slug?: string | null;
  /** Event type display name when source is holiday. */
  name?: string | null;
  /** Mantine color name for the banner strip. */
  color: string;
  /** Primary overlay headline. */
  headline: string;
  /** Optional supporting subtext. */
  subtext?: string | null;
  /** Relative media URL (`/api/media/{token}`) for the banner image. */
  imageUrl: string;
  /** Optional CTA route (evergreen); holiday banners typically omit this. */
  ctaRoute?: string | null;
  /** ISO calendar date (`yyyy-MM-dd`) of the holiday occurrence when source is holiday. */
  eventDateIso?: string | null;
  /** Whole days until the event date when source is holiday. */
  daysUntil?: number | null;
}

/** Pre-generated WebP carousel variants on the LCP payload. */
export interface PublicLandingLcpThumbnailTokens {
  w240?: string | null;
  w480?: string | null;
  w720?: string | null;
}

/**
 * Minimal LCP preload payload from GET /public/landing-lcp, including the
 * optional active seasonal banner so the landing page can avoid a second round-trip.
 */
export interface PublicLandingLcp {
  mediaType?: string | null;
  mediaToken?: string | null;
  posterMediaToken?: string | null;
  thumbnailTokens?: PublicLandingLcpThumbnailTokens | null;
  seasonalBanner?: PublicSeasonalBanner | null;
}

/**
 * Gets the API base URL from environment or default to relative path.
 */
const getApiBaseUrl = (): string => {
  return import.meta.env.VITE_API_URL || '/api';
};

/** Optional axios config for cancellable public landing fetches. */
export interface PublicFetchOptions {
  signal?: AbortSignal;
}

/**
 * Fetches a page of system card templates for public galleries (PBI #1118).
 */
export const getPublicTemplates = async (
  options: PublicCatalogPageOptions = {},
): Promise<PublicTemplatesPage> => {
  const baseUrl = getApiBaseUrl();
  const {
    eventTypeId,
    tagSlugs,
    page = 1,
    pageSize = CATALOG_PAGE_SIZE,
    signal,
  } = options;
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  if (eventTypeId != null) params.set('eventTypeId', String(eventTypeId));
  for (const slug of tagSlugs ?? []) {
    if (slug.trim()) params.append('tagSlugs', slug.trim());
  }
  const response = await axios.get<PublicTemplatesPage>(
    `${baseUrl}/public/templates?${params.toString()}`,
    { signal },
  );
  return response.data;
};

/**
 * One active system e-card for `/cards/:slug`. Unknown slugs return HTTP 404.
 */
export const getPublicCardTemplate = async (slug: string): Promise<PublicCardTemplate> => {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<PublicCardTemplate>(
    `${baseUrl}/public/templates/${encodeURIComponent(slug)}`,
  );
  return response.data;
};

/**
 * Fetches a page of the admin-curated landing carousel (assigned rows only).
 */
export const getPublicCarousel = async (
  options: Pick<PublicCatalogPageOptions, 'page' | 'pageSize' | 'signal'> = {},
): Promise<PublicTemplatesPage> => {
  const baseUrl = getApiBaseUrl();
  const { page = 1, pageSize = CATALOG_PAGE_SIZE, signal } = options;
  const params = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await axios.get<PublicTemplatesPage>(
    `${baseUrl}/public/carousel?${params.toString()}`,
    { signal },
  );
  return response.data;
};

/**
 * Fetches catalog tags for an occasion (tags that have at least one card). Anonymous-friendly.
 *
 * @param eventTypeSlug EVT-NNN slug for the active tab.
 */
export const getPublicEventTypeTags = async (eventTypeSlug: string): Promise<PublicEventTypeTag[]> => {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<PublicEventTypeTag[]>(
    `${baseUrl}/public/event-types/${encodeURIComponent(eventTypeSlug)}/tags`,
  );
  return response.data;
};

/** Options for public event-type discovery (PBI #1119 optional service filter). */
export interface GetPublicEventTypesOptions extends PublicFetchOptions {
  /**
   * When set, returns occasions with ≥1 item for that shop only
   * (`ecard` | `flowers` | `mailedCards`). Omit for Landing/Explore multi-service OR.
   */
  service?: string;
}

/**
 * Fetches public event types for Explore (OR) or a single catalog shop (`service`).
 *
 * @returns List of public event types
 */
export const getPublicEventTypes = async (
  options?: GetPublicEventTypesOptions,
): Promise<PublicEventType[]> => {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<PublicEventType[]>(`${baseUrl}/public/event-types`, {
    signal: options?.signal,
    params: options?.service ? { service: options.service } : undefined,
  });
  return response.data;
};

/**
 * Fetches a page of public flower products for galleries and landing strips (PBI #1118).
 * Empty page when the FlowerCatalog feature is disabled.
 */
export const getPublicFlowers = async (
  options: PublicCatalogPageOptions = {},
): Promise<PublicFlowersPage> => {
  const baseUrl = getApiBaseUrl();
  const {
    eventTypeId,
    page = 1,
    pageSize = CATALOG_PAGE_SIZE,
    signal,
  } = options;
  const params: Record<string, number> = { page, pageSize };
  if (eventTypeId != null) {
    params.eventTypeId = eventTypeId;
  }
  const response = await axios.get<PublicFlowersPage>(`${baseUrl}/public/flowers`, {
    params,
    signal,
  });
  return response.data;
};

/**
 * Single visible flower for a public PDP. Missing/hidden SKUs return HTTP 404.
 */
export const getPublicFlowerProduct = async (publicId: string): Promise<PublicFlowerProduct> => {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<PublicFlowerProduct>(
    `${baseUrl}/public/flowers/${encodeURIComponent(publicId)}`
  );
  return response.data;
};

/**
 * Submits a product interest ("Notify Me") from the landing page.
 * Includes a honeypot field for spam prevention.
 *
 * @param email - Email to notify
 * @param productName - Product they're interested in (e.g., "Flowers")
 * @param website - Honeypot field (should always be empty for real users)
 */
export const submitProductInterest = async (
  email: string,
  productName: string,
  website: string = ''
): Promise<void> => {
  const baseUrl = getApiBaseUrl();
  await axios.post(`${baseUrl}/public/interest`, { email, productName, website });
};

/** Payload for the public guest occasion-reminder lead magnet (PBI #1066). */
export interface GuestOccasionReminderPayload {
  email: string;
  personFirstName: string;
  occasionLabel: string;
  occasionMonth: number;
  occasionDay: number;
  website?: string;
}

/** One supported occasion for the public reminder dropdown (catalog event type). */
export interface GuestOccasionOption {
  name: string;
  /** Month (1-12) for fixed-date holidays; null otherwise. */
  fixedMonth?: number | null;
  /** Day (1-31) for fixed-date holidays; null otherwise. */
  fixedDay?: number | null;
}

/**
 * Loads system event types for the occasion-reminder dropdown.
 * Includes holidays without e-card templates (unlike /public/event-types).
 */
export const getGuestOccasionOptions = async (): Promise<GuestOccasionOption[]> => {
  const baseUrl = getApiBaseUrl();
  const response = await axios.get<GuestOccasionOption[]>(
    `${baseUrl}/public/occasion-reminders/occasions`,
  );
  return response.data;
};

/**
 * Submits a guest occasion reminder from the landing page.
 * Includes a honeypot field for spam prevention.
 */
export const submitGuestOccasionReminder = async (
  payload: GuestOccasionReminderPayload
): Promise<void> => {
  const baseUrl = getApiBaseUrl();
  await axios.post(`${baseUrl}/public/occasion-reminders`, payload);
};

/**
 * Confirms a guest occasion reminder via the double opt-in token.
 */
export const confirmGuestOccasionReminder = async (token: string): Promise<void> => {
  const baseUrl = getApiBaseUrl();
  await axios.post(`${baseUrl}/public/occasion-reminders/confirm`, null, {
    params: { token },
  });
};

/**
 * Unsubscribes a guest occasion reminder via the one-click token.
 */
export const unsubscribeGuestOccasionReminder = async (token: string): Promise<void> => {
  const baseUrl = getApiBaseUrl();
  await axios.post(`${baseUrl}/public/occasion-reminders/unsubscribe`, null, {
    params: { token },
  });
};

/** localStorage key for the hero template cache */
const HERO_CACHE_KEY = 'scheduledsmiles_hero_template';

/** Cache TTL in milliseconds (1 hour, matching server-side cache) */
const HERO_CACHE_TTL_MS = 60 * 60 * 1000;

/** Shape of the cached hero template entry in localStorage */
interface HeroCacheEntry {
  data: PublicCardTemplate;
  expiresAt: number;
}

/**
 * Reads the hero template from localStorage cache.
 * Returns the template if valid and not expired, null otherwise.
 * If allowExpired is true, returns expired entries as a fallback.
 */
const readHeroCache = (allowExpired = false): PublicCardTemplate | null => {
  try {
    const raw = localStorage.getItem(HERO_CACHE_KEY);
    if (!raw) return null;

    const entry: HeroCacheEntry = JSON.parse(raw);
    if (!allowExpired && Date.now() > entry.expiresAt) return null;

    return entry.data;
  } catch {
    return null;
  }
};

/**
 * Writes the hero template to localStorage cache with a TTL.
 */
const writeHeroCache = (data: PublicCardTemplate): void => {
  try {
    const entry: HeroCacheEntry = { data, expiresAt: Date.now() + HERO_CACHE_TTL_MS };
    localStorage.setItem(HERO_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // localStorage full or unavailable
  }
};

/**
 * Clears the hero template localStorage cache.
 * Call this when cached data is known to be stale (e.g., media token
 * returns 404, indicating the template's media was re-uploaded and
 * the cached mediaToken no longer exists in the database).
 */
export const invalidateHeroCache = (): void => {
  try {
    localStorage.removeItem(HERO_CACHE_KEY);
  } catch {
    // localStorage unavailable
  }
};

/**
 * Fetches the hero template fresh from the API, bypassing localStorage.
 * Used after cache invalidation to get the current mediaToken.
 *
 * @returns The featured template, or null if none is configured
 */
const fetchHeroTemplateFromApi = async (): Promise<PublicCardTemplate | null> => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get<PublicCardTemplate>(`${baseUrl}/public/hero-template`);

    if (response.status === 204 || !response.data) {
      return null;
    }

    writeHeroCache(response.data);
    return response.data;
  } catch {
    return null;
  }
};

/**
 * Fetches the featured hero template for the landing page animation.
 *
 * Caching strategy (three layers):
 * 1. Server-side IMemoryCache (1 hour) — avoids DB/blob hits
 * 2. HTTP Cache-Control header (1 hour) — avoids repeat API calls
 * 3. Client-side localStorage (1 hour) — offline fallback
 *
 * Returns cached data immediately if valid. On cache miss, fetches from API.
 * On API failure, falls back to expired cache data so the hero always renders.
 *
 * If the cached mediaToken is stale (media re-uploaded), the caller should
 * call invalidateHeroCache() then refetchHeroTemplate() to get fresh data.
 *
 * @returns The featured template, or null if none is configured
 */
export const getHeroTemplate = async (): Promise<PublicCardTemplate | null> => {
  const cached = readHeroCache();
  if (cached) return cached;

  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get<PublicCardTemplate>(`${baseUrl}/public/hero-template`);

    if (response.status === 204 || !response.data) {
      return null;
    }

    writeHeroCache(response.data);
    return response.data;
  } catch {
    // API failed — fall back to expired cache if available
    return readHeroCache(true);
  }
};

/**
 * Re-fetches the hero template, bypassing the localStorage cache.
 * Use this after invalidateHeroCache() to get fresh data when the
 * cached entry is known to be stale (e.g., media 404).
 *
 * @returns The featured template with current data, or null
 */
export const refetchHeroTemplate = fetchHeroTemplateFromApi;

/**
 * Builds a full media URL from a media token.
 * Reuses the same URL pattern as the rest of the app.
 *
 * @param mediaToken - The GUID media token
 * @returns Full URL for the media asset
 */
export const getPublicMediaUrl = (mediaToken: string): string => {
  // Prefer VITE_MEDIA_URL (Azure Front Door host) when set; fall back to
  // VITE_API_URL for local dev / E2E / rollback. See the matching helper
  // in cardTemplatesService.ts for the rationale and the normalisation
  // rules around the /api suffix.
  const baseUrl = getMediaBaseUrl();
  if (baseUrl.endsWith('/api')) {
    return `${baseUrl}/media/${mediaToken}`;
  }
  return `/api/media/${mediaToken}`;
};

/**
 * Resolves a seasonal-banner (or other public) image URL for display.
 * Absolute and blob URLs pass through; relative `/api/media/{token}` paths
 * are rewritten via {@link getPublicMediaUrl} so CDN hosts apply.
 *
 * @param imageUrl Relative `/api/media/{token}`, absolute URL, or blob URL.
 */
export const resolvePublicImageUrl = (imageUrl: string): string => {
  const trimmed = imageUrl.trim();
  if (!trimmed) return trimmed;

  if (
    trimmed.startsWith('http://')
    || trimmed.startsWith('https://')
    || trimmed.startsWith('blob:')
    || trimmed.startsWith('data:')
  ) {
    return trimmed;
  }

  const mediaMatch = trimmed.match(/\/api\/media\/([^/?#]+)/i);
  if (mediaMatch?.[1]) {
    return getPublicMediaUrl(mediaMatch[1]);
  }

  return trimmed;
};

/**
 * Fetches the active seasonal marketing banner for the landing page.
 * Returns null when no banner is in window (HTTP 204 or empty body).
 */
export const getPublicSeasonalBanner = async (
  options?: PublicFetchOptions,
): Promise<PublicSeasonalBanner | null> => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get<PublicSeasonalBanner | ''>(
      `${baseUrl}/public/seasonal-banner`,
      { signal: options?.signal },
    );

    if (response.status === 204 || !response.data) {
      return null;
    }

    return response.data as PublicSeasonalBanner;
  } catch {
    return null;
  }
};

/**
 * Fetches landing LCP metadata (first carousel tile + optional seasonal banner).
 * Returns null on failure so callers can fall back to a dedicated banner fetch.
 */
export const getPublicLandingLcp = async (
  options?: PublicFetchOptions,
): Promise<PublicLandingLcp | null> => {
  try {
    const baseUrl = getApiBaseUrl();
    const response = await axios.get<PublicLandingLcp>(`${baseUrl}/public/landing-lcp`, {
      signal: options?.signal,
    });

    if (response.status === 204 || !response.data) {
      return null;
    }

    return response.data;
  } catch {
    return null;
  }
};

/**
 * Resolves the base URL used to build /api/media/{token} URLs from the
 * public landing-page service (no auth headers).
 *
 * @see resolveMediaBaseUrl in cardTemplatesService.ts — same precedence
 * (VITE_MEDIA_URL ?? VITE_API_URL ?? '/api') and same /api normalisation.
 */
const getMediaBaseUrl = (): string => {
  const explicit = import.meta.env.VITE_MEDIA_URL as string | undefined;
  if (explicit) {
    const trimmed = explicit.replace(/\/$/, '');
    return trimmed.endsWith('/api') ? trimmed : `${trimmed}/api`;
  }
  return getApiBaseUrl();
};
